import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import api from "api/api";

//Material UI Components
import Box from "@mui/material/Box";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

// import mui icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

//Props Validations
import PropTypes from "prop-types";

import Header from "../common/Header";
import DriverAnalysisSummary from "../../components/driverAnalysis/DriverAnalysisSummary";
import DriverAnalysisMacroCategory from "./macroCategory/DriverAnalysisMacroCategory";
import DriverAnalysisHorizontalBarChart from "./variableCategory/DriverAnalysisHorizontalBarChart";

import DriverAnalysisCreateDialog from "./DriverAnalysisCreateDialog";

// assets
import fragranceImage from "../../assets/fragrance-image.png";
import efficacyImage from "../../assets/efficacy-image.png";
import sensoryAndApplicationImage from "../../assets/sensory&application-image.png";
import skinCare from "../../assets/skincare-image.png";
import stainsImage from "../../assets/stain-image.png";
import packagingImage from "../../assets/packaging-image.png";
import odourImage from "../../assets/odour-image.png";

import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import ViewToggle from "../../components/common/ViewToggle";

function CustomTabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

const imageData = {
    fragrance: {
        image: fragranceImage,
        color: "#FF7A00",
        imageStyle: {
            filter: "invert(67%) sepia(51%) saturate(6560%) hue-rotate(360deg) brightness(103%) contrast(109%)",
        },
    },
    efficacy: {
        image: efficacyImage,
        color: "#77B741",
        imageStyle: {
            filter: "invert(72%) sepia(4%) saturate(6726%) hue-rotate(48deg) brightness(89%) contrast(85%)",
        },
    },
    "sensory&application": {
        image: sensoryAndApplicationImage,
        color: "#429FF9",
        imageStyle: {
            filter: "invert(48%) sepia(96%) saturate(828%) hue-rotate(186deg) brightness(98%) contrast(99%)",
        },
    },
    skincare: {
        image: skinCare,
        color: "#FF6480",
        imageStyle: {
            filter: "invert(47%) sepia(83%) saturate(1771%) hue-rotate(318deg) brightness(112%) contrast(100%)",
        },
    },
    stains: {
        image: stainsImage,
        color: "#1FB6A7",
        imageStyle: {
            filter: "invert(65%) sepia(31%) saturate(7500%) hue-rotate(136deg) brightness(101%) contrast(76%)",
        },
    },
    packaging: {
        image: packagingImage,
        color: "#CF9FFF",
        imageStyle: {
            filter: "invert(69%) sepia(88%) saturate(1435%) hue-rotate(207deg) brightness(100%) contrast(102%)",
        },
    },
    odour: {
        image: odourImage,
        color: "#FF0000",
        imageStyle: {
            filter: "invert(18%) sepia(73%) saturate(5917%) hue-rotate(356deg) brightness(98%) contrast(120%)",
        },
    },
    overallopinion: {
        image: skinCare,
        color: "#FF6480",
        imageStyle: {
            filter: "invert(47%) sepia(83%) saturate(1771%) hue-rotate(318deg) brightness(112%) contrast(100%)",
        },
    },
};

function DriverAnalysisReport({ headerTitle = "Driver Analysis Report" }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reportId = window.location.href.split("id=").pop();

    const role = useSelector((state) => state.userStore.role) || null;
    const backendVariableVariableCategory = useSelector((state) => state.dataMappingStore.backendVariableVariableCategory);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellBeing = isHairCare || isSkinCare;

    // const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    // const backendVariableVariableCategory = useSelector((state) => state.dataMappingStore.backendVariableVariableCategory);
    // const [report, setReport] = useState([]);
    const [targetVariableOptions, setTargetVariableOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [variableKpi, setVariableKpi] = useState([]);

    const [output, setOutput] = useState({});
    const [report, setReport] = useState([]);
    // const [coreData, setCoreData] = useState([]);
    const [studyRef, setStudyRef] = useState("");
    const [studyPeriod, setStudyPeriod] = useState("");
    const [targetKpiLabel, setTargetKpiLabel] = useState("");
    const [plsVarianceValues, setPlsVarianceValues] = useState("");

    // toggle
    const [isVariableKpiShown, setIsVariableKpiShown] = useState(true);

    const [isDisabledObj, setIsDisabledObj] = useState({
        calculate: true,
        store: false,
    });

    const generateRandomColor = () => {
        const randomColor = `#${Math.floor(Math.random() * 16777215)
            .toString(16)
            .padStart(6, "0")}`;
        return randomColor;
    };

    useEffect(() => {
        const fetchDriverAnalysisResult = async () => {
            try {
                await api
                    .get(process.env.REACT_APP_SERVER_PATH + `/driver-analysis-output/${reportId}/`)
                    .then((res) => {
                        if (res.data && res?.data?.result) {
                            const reportData = res.data;
                            setOutput(reportData);
                            setIsDisabledObj({
                                calculate: res?.data?.saved || res.data?.is_bv_recalc_required,
                                store: res?.data?.saved,
                            });

                            const _targetVariableOption = [];
                            const tempArr = [];

                            // const coreDataIds = reportData.result?.core_data_ids?.split(",");
                            // fetchCoreData(coreDataIds);

                            setTargetKpiLabel(`${reportData.target_kpi_label} (${reportData.target_kpi})`);

                            // macro category view data prep
                            const outputResult = JSON.parse(reportData.result);
                            if (reportData.algorithm === "partial_least_squares") {
                                const varianceExplained = outputResult.find(item => item.variance_explained)?.variance_explained;
                                const varianceValues = {
                                    x_variance: varianceExplained.x_variance,
                                    y_variance: varianceExplained.y_variance
                                };
                                setPlsVarianceValues(varianceValues);
                            }
                            const macroCategoryData = outputResult.filter(item => item.macroCategory && item.subScores);

                            const _result = macroCategoryData.map((datum) => {
                                const options = datum.subScores.map((option) => {
                                    const backendVariableCategory = backendVariableVariableCategory?.[option.backendVariable] || "(undefined)";
                                    tempArr.push({
                                        [`${option.displayLabel}*-*${backendVariableCategory}`]: option.score.toFixed(3),
                                    });

                                    _targetVariableOption.push({
                                        backendVariable: option.backendVariable,
                                        label: option.displayLabel,
                                    });
                                    return `${option.displayLabel} (${option.score.toFixed(3)})*-*${backendVariableCategory}`;
                                });
                                const _macroCategory = datum.macroCategory.replace(/\s/g, "").toLowerCase();
                                const isMacroCategoryPresent = _macroCategory in imageData ? true : false;
                                return {
                                    name: datum.macroCategory,
                                    value: datum.score.toFixed(3),
                                    image: isMacroCategoryPresent ? imageData[_macroCategory]?.image : null,
                                    options: options,
                                    color: isMacroCategoryPresent ? imageData[_macroCategory].color : generateRandomColor(),
                                    imageStyle: isMacroCategoryPresent ? imageData[_macroCategory].imageStyle : null,
                                };
                            });
                            setReport(_result);

                            _targetVariableOption.push({
                                backendVariable: reportData.target_kpi,
                                label: reportData.target_kpi_label,
                            });

                            setTargetVariableOptions(_targetVariableOption);
                            setVariableKpi(tempArr);

                            const info = JSON.parse(reportData.info);
                            setProductOptions(info?.studyProduct || []);
                            setStudyPeriod(info?.studyPeriod.join(",") || "");
                            setStudyRef(info?.studyReference.join(",") || "");
                        }
                    })
                    .catch((error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        };

        fetchDriverAnalysisResult();
    }, [reportId]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const handleStoreDriverAnalysisDialogueOpen = async () => {
        const url = process.env.REACT_APP_SERVER_PATH + `/driver-analysis-output/${reportId}/`;
        await api
            .patch(url, { saved: true })
            .then(() => {
                handleError("success", `Your report ${output.identifier} will be stored within 'Saved' view`);
            })
            .catch((error) => {
                handleError("error", error);
            });
    };

    return (
        <>
            <Header title={headerTitle} />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <DriverAnalysisSummary
                    studyRef={studyRef}
                    studyPeriod={studyPeriod}
                    targetKpi={targetKpiLabel}
                    identifier={output.identifier}
                    rSquaredValue={output.overall_accuracy}
                    plsVarianceValues={plsVarianceValues}
                    algorithm={output.algorithm}
                />
            </div>

            <div
                style={{
                    minHeight: "calc(100vh - 170px)",
                    padding: "20px",
                    margin: "20px",
                    marginTop: "35px",
                }}
                className="full-container m-4 background-color-white  box-shadow--full-container"
            >
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                    }}
                >
                    <div
                        className="p-2 liner-light-blue-background color-white"
                        style={{
                            borderRadius: "50%",
                            position: "relative",
                            top: -35,
                            left: -35,
                            cursor: "pointer",
                            zIndex: 10,
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <span>
                            <ArrowBackIcon className="color-white" />
                        </span>
                    </div>
                </div>
                <div
                    className="card-btns"
                    style={{
                        padding: "0px 0px",
                        margin: "10px 0px",
                    }}
                >
                    <div
                        className=" d-flex"
                        style={{ alignItems: "center" }}
                    >
                        <div
                            className=" d-flex"
                            style={{
                                alignItems: "center",
                                margin: "0px 10px",
                            }}
                        >
                            <ViewToggle
                                value={isVariableKpiShown}
                                setValue={setIsVariableKpiShown}
                                labels={["Variable/Kpi", "Macro Category"]}
                            />
                        </div>
                    </div>
                    {((role && role.toLowerCase() === "admin") || isBeautyAndWellBeing) && (
                        <div
                            style={{
                                display: "flex",
                            }}
                        >
                            <DriverAnalysisCreateDialog
                                disabled={isDisabledObj.calculate}
                                targetVariableOptions={targetVariableOptions}
                                productOptions={productOptions}
                                reportData={output}
                            />
                            <span>
                                <Button
                                    size="small"
                                    className={isDisabledObj.store ? "custom-btn  disabled" : "custom-btn custom-btn-outlined color-blue"}
                                    variant="outlined"
                                    startIcon={<SaveOutlinedIcon />}
                                    onClick={handleStoreDriverAnalysisDialogueOpen} // Ensure this triggers the open function
                                    disabled={isDisabledObj.store}
                                >
                                    Store Driver Analysis
                                </Button>
                            </span>
                        </div>
                    )}
                </div>
                {isVariableKpiShown ? <DriverAnalysisHorizontalBarChart variableKpi={variableKpi}
                    algorithm={output?.algorithm} /> : <DriverAnalysisMacroCategory data={report} />}

            </div>
        </>
    );
}

// Typechecking props for the DriverAnalysis
DriverAnalysisReport.propTypes = {
    headerTitle: PropTypes.string,
};

export default DriverAnalysisReport;
